// Layout/shellNav — the one place the shell decides WHAT is in the navigation:
// the industry- and role-gated items from constants/uiRegistry, the AI Proposals
// entry for pilot tenants (with its live count), the platform tools for Abon staff,
// grouped Work / Money / Setup. The rail (desktop) and the dock (phone) both read
// this, so they can never disagree about what a tenant can reach.
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../App';
import { getNavForIndustry, getIndustryKey } from '../../constants/uiRegistry';
import { useIsPlatformStaff } from '../Auth/RequireSuperuser';
import { getUserRole } from '../../helpers/tenantHelpers';
import { isAiProposalsEnabled } from '../../helpers/agentFeature';
import { listProposals } from '../../api/proposalsApi';

// Grouping is by what the page is FOR, not by the registry's order. A path the
// registry adds later lands in Work until someone files it.
export const GROUPS = [
  { key: 'work', label: 'Work', paths: ['/dashboard', '/ai/proposals', '/leads', '/customers', '/schedule', '/communication', '/operations'] },
  { key: 'money', label: 'Money', paths: ['/revival', '/finance', '/sales', '/analytics', '/marketing'] },
  { key: 'setup', label: 'Setup', paths: ['/team', '/settings', '/platform/billing', '/platform/health'] },
];

const groupOf = (path) => GROUPS.find((g) => g.paths.includes(path))?.key || 'work';

export const isActivePath = (pathname, path) => pathname === path || pathname.startsWith(`${path}/`);

export default function useShellNav() {
  const { user, tenant } = useContext(AuthContext);
  const location = useLocation();
  const { isStaff } = useIsPlatformStaff();

  const industryKey = useMemo(() => getIndustryKey((tenant?.industry || 'general').toLowerCase()), [tenant?.industry]);
  const userRole = getUserRole(user?.role || 'Member');

  const [items, setItems] = useState([]);
  const [aiBadge, setAiBadge] = useState(0);

  useEffect(() => {
    const update = () => {
      const base = getNavForIndustry(industryKey, userRole);
      setItems(Array.isArray(base) ? base : []);
    };
    update();
    const onStorage = (e) => { if (e.key === 'activeTenant' || e.key === 'ui_overrides') update(); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [industryKey, userRole]);

  // Pending-proposal count for the badge (pilot tenants only). Re-polls on
  // navigation and when the proposals inbox confirms/declines something.
  useEffect(() => {
    if (!isAiProposalsEnabled()) { setAiBadge(0); return undefined; }
    let alive = true;
    const fetchCount = async () => {
      try {
        const { data } = await listProposals({ status: 'pending', limit: 1 });
        if (alive) setAiBadge(data?.counts?.total || 0);
      } catch { /* transient — keep the last count */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    window.addEventListener('ai-proposals:changed', fetchCount);
    return () => { alive = false; clearInterval(t); window.removeEventListener('ai-proposals:changed', fetchCount); };
  }, [industryKey, location.pathname]);

  const all = useMemo(() => {
    const out = [...items];
    if (isAiProposalsEnabled() && !out.some((i) => i.path === '/ai/proposals')) {
      const idx = out.findIndex((i) => i.path === '/dashboard');
      out.splice(idx >= 0 ? idx + 1 : 0, 0, { path: '/ai/proposals', label: 'AI Proposals', icon: 'FiZap' });
    }
    if (isStaff) {
      out.push({ path: '/platform/billing', label: 'Platform billing', icon: 'FiDollarSign' });
      out.push({ path: '/platform/health', label: 'Platform health', icon: 'FiBarChart2' });
    }
    return out.map((i) => ({
      ...i,
      group: groupOf(i.path),
      badge: i.path === '/ai/proposals' ? aiBadge : i.badge || 0,
      active: isActivePath(location.pathname, i.path),
    }));
  }, [items, isStaff, aiBadge, location.pathname]);

  const groups = useMemo(
    () => GROUPS.map((g) => ({ ...g, items: all.filter((i) => i.group === g.key) })).filter((g) => g.items.length),
    [all]
  );

  const current = all.find((i) => i.active) || null;

  return { items: all, groups, current, industryKey, userRole };
}
